import { useContext } from 'react'

import { AppStateContext } from '../../../state/AppProvider'

import Grid from '@mui/material/Grid2'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'

import { FormControl, FormHelperText } from '@mui/material'
import { first, includes, isEmpty } from 'lodash'
import { ChangeSourceEnum, FormField, updateExperimentFormField } from '../../../api'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import { FormFieldLabel } from '../FormFieldLabel'

import styles from './SelectFormFields.module.css'

interface Props {
  id?: string
  name: string
  stepName: string
  fieldName: string
  convId: string
  gridSize: number
  data: FormField<string>
  url?: string
  tooltipInfo?: React.ReactNode
  disabled?: boolean
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
}

export const SelectFormField = ({
  name,
  stepName,
  fieldName,
  convId,
  gridSize,
  data,
  url,
  tooltipInfo,
  disabled = false,
  onError,
}: Props) => {
  const appStateContext = useContext(AppStateContext)

  async function handleChange(event: SelectChangeEvent) {
    try {
      const newValue = event.target.value as string
      const newConversation = await updateExperimentFormField(
        stepName,
        fieldName,
        newValue,
        convId,
        appStateContext?.state.shouldValidateForm ?? false,
        appStateContext?.state.frontendSettings || undefined,
      )

      if (newConversation) {
        const messages = appStateContext?.state.currentChat?.messages || []
        newConversation.messages = messages

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConversation })
      }
    } catch (error) {
      const errorWithReactElement = error as Error & { reactElement?: React.ReactNode }
      onError?.({
        title: 'Error',
        description:
          errorWithReactElement?.reactElement ||
          (error instanceof Error ? error.message : 'An error occurred while updating the field'),
        type: 'error',
      })
    }
  }

  if (data?.hidden === true) {
    return <></>
  }

  const isUnavailableValue = !includes(data.options, data?.field_value) && !isEmpty(data?.field_value)

  return (
    <Grid
      size={gridSize}
      id={`field_${fieldName}`}>
      <FormFieldLabel
        text={name}
        isAiGenerated={data?.change_source !== ChangeSourceEnum.MANUAL}
        linkTitle="Download Template"
        underlineLink={true}
        url={url}
        tooltipInfo={tooltipInfo}
      />

      <FormControl
        fullWidth
        error={!isEmpty(data.errors)}>
        {isEmpty(data?.field_value) && <InputLabel shrink={false}>Select</InputLabel>}
        <Select
          disabled={disabled}
          classes={{
            error: isEmpty(data.errors) ? undefined : styles.errorInput,
            disabled: styles.disabledSelectField,
          }}
          value={data?.field_value || ''}
          onChange={handleChange}
          MenuProps={{
            PaperProps: {
              className: styles.menuPaper,
            },
          }}>
          {isUnavailableValue && (
            <MenuItem
              key={String(data.field_value)}
              value={String(data.field_value)}
              className={styles.menuItem}
              disabled>
              {data?.field_value}
            </MenuItem>
          )}
          {data.options?.map(option => (
            <MenuItem
              key={option}
              value={option}
              className={styles.menuItem}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{first(data.errors)}</FormHelperText>
      </FormControl>
    </Grid>
  )
}
